import { HateoasResourceService, Resource, ResourceCollection } from '@lagoshny/ngx-hateoas-client';
import { Resource as BaseResource } from '@lagoshny/ngx-hateoas-client';
import { v4 as uuidv4 } from 'uuid';
import { lastValueFrom } from 'rxjs';
import { HttpHeaders } from '@angular/common/http';

class NodePath {
  static create(path: NodePath | string): NodePath {
    if (path instanceof NodePath) return path;
    return new NodePath(path);
  }

  public static readonly Root: NodePath = new NodePath('$');

  constructor(public raw: string) {
    const splitPath = raw.split('.');

    this.depth = [...splitPath].length;
    this.name = [...splitPath].pop();

    if (this.depth > 1) {
      const parentRaw = [...splitPath].slice(0, -1).join('.');
      this.parent = new NodePath(parentRaw);
    }
  }

  getParentAtDepth(depth: number): NodePath {
    const maxDepth = this.depth;
    if (depth < 1) throw `Unable to get path depth lower than 1 for ${this.raw}`;
    if (depth > maxDepth)
      throw `Unable to get parent path at depth ${depth}. Path at ${this.raw} only has ${maxDepth} levels.`;

    let pathAtResolvedDepth: NodePath = this;
    for (let d = maxDepth - 1; d >= depth; d--) {
      pathAtResolvedDepth = pathAtResolvedDepth.parent;
    }
    return pathAtResolvedDepth;
  }

  isMatch(path: NodePath) {
    return this.raw == path.raw;
  }

  readonly depth: number;
  readonly name: string;
  readonly parent?: NodePath;
}

export interface RootResourceTreeCriteriaOptions {
  useCache?: boolean;
  params?: any;
  headers?: HttpHeaders;
}

export interface ResourceTreeCriteriaOptions extends RootResourceTreeCriteriaOptions {
  isArray?: boolean;
  link?: string;
  headers?: HttpHeaders;
}

export class ResourceTreeCriteria<T extends Resource> {
  constructor(
    public resourceFactory: new () => T,
    public rootUrl: string,
    public options?: RootResourceTreeCriteriaOptions,
  ) {}

  public readonly nodesCriteria: Array<ResourceTreeNodeCriteria> = [];

  include(path: string, options?: ResourceTreeCriteriaOptions): ResourceTreeCriteria<T> {
    const nodeCriteria = new ResourceTreeNodeCriteria(path, options);
    this.nodesCriteria.push(nodeCriteria);
    return this;
  }

  findNodesCriteriaByPath(path: string): ResourceTreeNodeCriteria {
    return this.nodesCriteria.find(c => c.path.raw == path);
  }

  getChildNodesCriteria(path: NodePath) {
    const childNodesCriteria = this.nodesCriteria.filter(n => {
      const nodeParentPath = n.path.parent;
      if (!nodeParentPath) return false;
      return nodeParentPath.isMatch(path);
    });
    return childNodesCriteria;
  }
}

export class ResourceTreeNodeCriteria {
  path: NodePath;

  constructor(
    path: string,
    public options?: ResourceTreeCriteriaOptions,
  ) {
    this.path = new NodePath(path);
  }
}

export class ResourceTree<TRootResource extends Resource> {
  constructor(
    private criteria: ResourceTreeCriteria<TRootResource>,
    private hateoasResourceService: HateoasResourceService,
  ) {}

  async load(): Promise<TRootResource> {
    const rootResourceLoader = new RootResourceLoader<TRootResource>(
      this.hateoasResourceService,
      this.criteria.resourceFactory,
      this.criteria.rootUrl,
      this.criteria.options,
    );

    const resource = await rootResourceLoader.load();

    const resources = [resource];

    await this.loadResourceChildren(NodePath.Root, resources);

    return resource;
  }

  private async loadSpecificChildResources(
    parents: Array<BaseResource>,
    childNodesCriteria: Array<ResourceTreeNodeCriteria>,
  ) {
    const relatedResourceLoadPromises: Array<Promise<ResourcesPath>> = [];
    for (const childNodeCriteria of childNodesCriteria) {
      for (const parent of parents) {
        const relatedResourceLoader = new RelatedResourceLoader(
          parent,
          childNodeCriteria.path,
          childNodeCriteria.options,
        );
        const relatedResourceLoadPromise = relatedResourceLoader.load();
        relatedResourceLoadPromises.push(relatedResourceLoadPromise);
      }
    }

    // Recursion fun
    const childLoadingPromises: Array<Promise<void>> = [];
    for (const relatedResourceLoadPromise of relatedResourceLoadPromises) {
      const resourcePath = await relatedResourceLoadPromise;
      if (!resourcePath) continue;
      const loadChildResourcesPromise = this.loadResourceChildren(resourcePath.path, resourcePath.resources);
      childLoadingPromises.push(loadChildResourcesPromise);
    }
    await Promise.all(childLoadingPromises);
  }

  // Example:
  // Path: '$.borrowers.contact' and contacts for all borrowers will be loaded
  async loadPath(root: TRootResource, path: string) {
    const parents = this.getParents(root, path);
    await this.loadPathForParents(path, parents);
  }

  // Example:
  // Path: '$.borrowers.contact' and parent borrowers (1...*) would load the contact
  // and contact child resources for those borrowers
  // Note parents are required.
  async loadPathForParents(path: string, parents: Array<BaseResource>) {
    const childNodeCriteria = this.criteria.findNodesCriteriaByPath(path);
    await this.loadSpecificChildResources(parents, [childNodeCriteria]);
  }

  // Example:
  // Path: '$.borrowers.contact' will load all child data.
  // A resource is required.
  async loadResourceChildren(path: string | NodePath, resources: Array<BaseResource>) {
    const nodePath = NodePath.create(path);
    const childNodesCriteria = this.criteria.getChildNodesCriteria(nodePath);
    await this.loadSpecificChildResources(resources, childNodesCriteria);
  }

  // TODO: loadResourceOnParent. Path + Resource. Pull resource via self link, load
  // its children (via loadResourceChildren) then replace the resource on the
  // tree (that part's tricky).

  // Will return parent objects matching the path.
  // Note: the return type is always an array, even if only one parent is expected
  // Examples:
  // '$.borrowers.contacts' will return an array of all contacts
  // '$.borrowers.contacts.addresses' will return an array of all addresses
  getResources<TResource extends BaseResource>(root: TRootResource, path: string): Array<TResource> {
    const currentPath = NodePath.Root;
    const targetPath = new NodePath(path);
    const parents = [root];
    return this.getChildResources(currentPath, targetPath, parents) as Array<TResource>;
  }

  private getChildResources(path: NodePath, targetPath: NodePath, parents: Array<BaseResource>): Array<BaseResource> {
    const currentDepth = path.depth;
    const targetDepth = targetPath.depth;

    // Goal reached, stop the recursion
    if (currentDepth == targetDepth) return parents;

    const nextDepth = currentDepth + 1;
    const nextPath = targetPath.getParentAtDepth(nextDepth);

    // Get the children for the next path
    const children: Array<BaseResource> = [];
    for (const parent of parents) {
      const childValue = parent[nextPath.name];
      if (!childValue) continue;
      const childValues = Array.isArray(childValue) ? childValue : [childValue];
      children.push(...childValues);
    }

    // The children from this depth become the parents for the next
    const nextChildren = this.getChildResources(nextPath, targetPath, children);
    return nextChildren;
  }

  getParents<TParent extends BaseResource>(root: TRootResource, path: string): Array<TParent> {
    const childPath = new NodePath(path);
    const parentPath = childPath.parent;
    return this.getResources(root, parentPath.raw);
  }
}

class RootResourceLoader<T extends Resource> {
  constructor(
    private hateoasResourceService: HateoasResourceService,
    private resourceFactory: new () => T,
    private rootUrl: string,
    private options?: ResourceTreeCriteriaOptions,
  ) {}

  async load(): Promise<T> {
    const resource$ = this.hateoasResourceService.getResource<T>(this.resourceFactory, this.rootUrl, {
      useCache: this.options?.useCache,
      headers: this.options?.headers,
    });

    const resource = await lastValueFrom(resource$);
    return resource;
  }
}

class ResourcesPath {
  constructor(
    public resources: Array<BaseResource>,
    public path: NodePath,
  ) {}
}

class RelatedResourceLoader {
  constructor(
    private parent: BaseResource,
    private path: NodePath,
    private options?: ResourceTreeCriteriaOptions,
  ) {}

  async load() {
    const relationName = this.options?.link || this.path.name;

    if (this.options?.isArray) {
      return await this.loadRelatedCollection(relationName);
    }
    return await this.loadRelated(relationName);
  }

  private getOptionsParams(options: ResourceTreeCriteriaOptions): any {
    const useCache = this.options?.useCache ?? false;
    let params = this.options?.params;

    if (!useCache) {
      params = params || {};
      params.noCache = uuidv4();
    }
    return params;
  }

  private async loadRelatedCollection(relationName: string) {
    if (!this.parent.hasRelation(relationName)) return null;

    const useCache = this.options?.useCache ?? false;
    const params = this.getOptionsParams(this.options);

    const related$ = this.parent.getRelatedCollection<ResourceCollection<Resource>>(relationName, {
      useCache,
      params,
      headers: this.options?.headers,
    });
    const related = await lastValueFrom(related$);

    const propertyName = this.path.name;
    this.parent[propertyName] = related.resources;

    return new ResourcesPath(related.resources, this.path);
  }

  private async loadRelated(relationName: string) {
    if (!this.parent.hasRelation(relationName)) return null;

    const useCache = this.options?.useCache ?? false;
    const params = this.getOptionsParams(this.options);

    const related$ = this.parent.getRelation<Resource>(relationName, {
      useCache,
      params,
      headers: this.options?.headers,
    });

    const resource = await lastValueFrom(related$);

    const propertyName = this.path.name;
    this.parent[propertyName] = resource;

    return new ResourcesPath([resource], this.path);
  }
}
